import lowerFirst from 'lodash/lowerFirst';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import moment from 'moment';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { INPUT_TEXT_TYPES } from 'ui/inputs';
import { BannerVariants } from 'ui/banner/types';
import { FormField } from 'ui/formField';
import { LargeSegmentedControl } from 'ui/segmentedControl';
import noop from 'utils/noop';
import { setTime } from 'utils/time';
import { useFieldMappingsContext } from 'modules/workflows/context/FieldMappingsContext';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  getEventFieldMappings,
  getIsJourneyFieldMappingsEnabled,
  getIsNodeEditorReadOnly,
  getWorkflow,
  updateNodeData,
} from 'modules/workflows/ducks';
import { DelayNodeData, DelayUsingProperty } from 'modules/workflows/types';
import { EditorBaseProps } from '../../types';
import { ToggleSection } from '../ToggleSection';
import { SkipDaysOfWeekForm } from '../formElements';
import {
  DAY_CONFIG,
  DELAY_TYPES,
  DELAY_TYPE_OPTIONS,
  DESCRIPTIONS,
  HOUR_CONFIG,
  MINUTE_CONFIG,
  SCHEDULE_TYPES,
} from './DelayEditor.constants';
import { DelayInputText, DelayTimeWrapper, Description, StyledBanner } from './DelayEditor.styles';
import { DelayTimeConfig } from './DelayEditor.types';
import {
  getDelayEditorNodeDataToBeCompared,
  getNumber,
  getPlural,
  getTodTime,
  getTodTimestamp,
  isValidNumber,
  parseTodTime,
} from './DelayEditor.utils';
import { DelayUsingPropertyForm, TimeOfDayForm } from './components';

export const DELAY_TIME_FIELD_ID = 'delay-time-field';
export const DELAY_USING_TIME_ID = 'segmented-control-option-time';
export const DELAY_USING_PROPERTY_ID = 'segmented-control-option-property';
export const USING_PROPERTY_FIELD = 'basic-select-trigger-select-property-data-type';
export const DELAY_BUTTON_GROUP = 'button-group-is-additive-button-group';
export const DELAY_ADDITIVE_ID = 'group-button-is-additive-true';
export const DELAY_NOT_ADDITIVE_ID = 'group-button-is-additive-false';
export const DELAY_INPUT_FIELD_ID = 'delay-time-wrapper';
export const DELAY_DAYS_ID = 'delay-days';
export const DELAY_HOURS_ID = 'delay-hours';
export const DELAY_MINUTES_ID = 'delay-minutes';
export const DELAY_FURTHER_ID = 'add-extra-delay';
export const DAYS_SKIPPED_ID = 'skip-send-on';

export type DelayEditorProps = EditorBaseProps<DelayNodeData>;

export const DelayEditor = ({
  nodeData = {
    nodeClass: '',
    nodeSuperClass: '',
  },
  nodeId,
  clonedFromNodeData,
  ...rest
}: DelayEditorProps) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);
  const { fieldMappings } = useFieldMappingsContext();
  const mappingsOld = useSelector(getEventFieldMappings);
  const mappings = isJourneyFieldMappingsEnabled ? fieldMappings : mappingsOld;
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  /**
   * Delay section
   *
   * NOTE: Need to change null defaults to undefined, per console warning
   */

  const [delayDays, setDelayDays] = useState(getNumber(nodeData.delayDays));
  const [delayHours, setDelayHours] = useState(getNumber(nodeData.delayHours));
  const [delayMinutes, setDelayMinutes] = useState(getNumber(nodeData.delayMinutes));

  //
  // ─── DELAY OPTIONS ──────────────────────────────────────────────────────────────
  //
  const delayOptions = useMemo(
    () => DELAY_TYPE_OPTIONS.map((o) => ({ ...o, disabled: isTileEditorReadOnly })),
    [isTileEditorReadOnly],
  );

  //
  // ─── DELAY USING PROPERTY ───────────────────────────────────────────────────────
  //

  const [delayType, setDelayType] = useState(
    nodeData.delayUsingProperty ? DELAY_TYPES.PROPERTY : DELAY_TYPES.TIME,
  );
  const [dupIsAdditive, setDupIsAdditive] = useState(nodeData.delayUsingProperty?.delayIsAdditive);
  // For some reason we're getting back an uppercased value, so this corrects it
  const [dupDataType, setDupDataType] = useState(
    nodeData.delayUsingProperty?.dataType
      ? lowerFirst(nodeData.delayUsingProperty?.dataType)
      : null,
  );
  const [dupField, setDupField] = useState(nodeData.delayUsingProperty?.field);

  const isUsingProperty = delayType === DELAY_TYPES.PROPERTY;

  //
  // ─── DELAY UNTIL ────────────────────────────────────────────────────────────────
  //

  const [isDelayedFurther, setIsDelayedFurther] = useState(!!nodeData.timeOfDay);
  const [todTimestamp, setTodTimestamp] = useState(getTodTimestamp(nodeData.timeOfDay));
  const [todTime, setTodTime] = useState(
    getTodTime(nodeData.timeOfDay?.hour, nodeData.timeOfDay?.minute),
  );
  const [scheduleType, setScheduleType] = useState(
    nodeData.timeOfDay?.scheduleType || SCHEDULE_TYPES.PROJECT_TIME_ZONE,
  );

  //
  // ─── SKIP SEND DAYS ─────────────────────────────────────────────────────────────
  //

  const [daysAreSkipped, setDaysAreSkipped] = useState(!!nodeData.skipDaysOfWeek);
  const [skipDaysOfWeek, setSkipDaysOfWeek] = useState(nodeData.skipDaysOfWeek);

  const handleDelayTimeUpdate = (
    e: string,
    config: DelayTimeConfig,
    setterFn: Dispatch<SetStateAction<number | undefined>> = noop,
  ) => {
    if (isValidNumber(e, config)) {
      // Get rid of the leading 0
      // Floor it to get rid of the decimal point
      setterFn(Math.floor(Number(e)));
    }
  };

  const getUpdatedNodeData = () => ({
    ...nodeData,
    delayDays: getNumber(delayDays) || null,
    delayHours: getNumber(delayHours) || null,
    delayMinutes: getNumber(delayMinutes) || null,
    delayUsingProperty:
      isUsingProperty && dupField
        ? {
            dataType: dupDataType,
            delayIsAdditive: dupIsAdditive || false,
            field: dupField,
          }
        : null,
    timeOfDay: isDelayedFurther
      ? {
          ...parseTodTime(todTime),
          scheduleType,
          timestamp: todTimestamp ? setTime(moment.utc(todTimestamp), todTime).valueOf() : null,
        }
      : null,
    skipDaysOfWeek: daysAreSkipped ? skipDaysOfWeek : null,
  });

  const handleUpdate = () => {
    dispatch(updateNodeData(getUpdatedNodeData(), nodeId));
  };

  const initialNodeData = useMemo(() => getDelayEditorNodeDataToBeCompared(nodeData), [nodeData]);
  const updatedNodeData = getDelayEditorNodeDataToBeCompared(
    getUpdatedNodeData() as Partial<DelayNodeData>,
  );

  const handleDelayTypeChange = (type: string) => setDelayType(type);

  const clonedFields = clonedFromNodeData?.delayUsingProperty;
  // data handling of cloned journey with different schema
  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData: isUsingProperty ? clonedFields : {},
    fieldsToValidate: {
      field: {
        isValid: dupField,
      },
    },
  });

  // checks the cloned delayUsingProperty data to see if it's valid in current project
  const clonedDelayUsingPropertyValidation = (cloned: DelayUsingProperty) => {
    if (mappings?.[cloned?.dataType]?.[cloned.field]) {
      setDupDataType(cloned?.dataType);
      setDupField(cloned?.field);
    }
  };

  useEffect(() => {
    // find a more reliable way to determine if initialNodeData has been validated
    if (clonedFields && initialNodeData?.delayUsingProperty === undefined) {
      clonedDelayUsingPropertyValidation(clonedFields);
      setDelayType(DELAY_TYPES.PROPERTY);
      setDupIsAdditive(clonedFields?.delayIsAdditive);
    }
  }, [clonedFields, initialNodeData]);

  return (
    <NodeEditor
      {...rest}
      onUpdate={handleUpdate}
      /** These are used to check if node has changes */
      initialNodeData={initialNodeData}
      updatedNodeData={updatedNodeData}
      canUpdate={!isTileEditorReadOnly}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      {workflow?.enabled && !isTileEditorReadOnly && (
        <StyledBanner variant={BannerVariants.WARNING}>
          Only new contacts entering the tile will be affected by these changes
        </StyledBanner>
      )}

      <FormField isRequired={false} inputId={DELAY_TIME_FIELD_ID} data-test={DELAY_TIME_FIELD_ID}>
        <LargeSegmentedControl
          data-test="local-match-triggering-event-fields"
          isFullWidth={true}
          onSelect={handleDelayTypeChange}
          options={delayOptions}
          value={delayType}
        />

        <Description>{DESCRIPTIONS[delayType]}</Description>
        {isUsingProperty && (
          <DelayUsingPropertyForm
            delayIsAdditive={!!dupIsAdditive}
            dataType={dupDataType ?? ''}
            field={dupField ?? ''}
            mappings={mappings}
            onIsAdditiveChange={setDupIsAdditive}
            onDataTypeChange={setDupDataType}
            onFieldChange={setDupField}
            isError={cloneErrors?.field}
            isReadOnly={isTileEditorReadOnly}
            displayValue={cloneDisplayValues?.field}
          />
        )}
        {/* todo: WF-7726 Replace with src/modules/workflowNodeEditors/components/formElements/DurationForm/DurationForm.tsx */}
        <DelayTimeWrapper id={DELAY_INPUT_FIELD_ID} data-test={DELAY_INPUT_FIELD_ID}>
          <DelayInputText
            id={DELAY_DAYS_ID}
            data-test={DELAY_DAYS_ID}
            placeholder="0"
            type={INPUT_TEXT_TYPES.NUMBER}
            value={`${delayDays ?? ''}`}
            Suffix={<span>{getPlural(delayDays, 'day')}</span>}
            isReadOnly={isTileEditorReadOnly}
            onChange={(e) => handleDelayTimeUpdate(e, DAY_CONFIG, setDelayDays)}
            {...DAY_CONFIG}
          />
          <DelayInputText
            id={DELAY_HOURS_ID}
            data-test={DELAY_HOURS_ID}
            placeholder="0"
            type={INPUT_TEXT_TYPES.NUMBER}
            value={`${delayHours ?? ''}`}
            Suffix={<span>{getPlural(delayHours, 'hour')}</span>}
            isReadOnly={isTileEditorReadOnly}
            onChange={(e) => handleDelayTimeUpdate(e, HOUR_CONFIG, setDelayHours)}
            {...HOUR_CONFIG}
          />
          <DelayInputText
            id={DELAY_MINUTES_ID}
            data-test={DELAY_MINUTES_ID}
            placeholder="0"
            type={INPUT_TEXT_TYPES.NUMBER}
            value={`${delayMinutes ?? ''}`}
            Suffix={<span>{getPlural(delayMinutes, 'minute')}</span>}
            isReadOnly={isTileEditorReadOnly}
            onChange={(e) => handleDelayTimeUpdate(e, MINUTE_CONFIG, setDelayMinutes)}
            {...MINUTE_CONFIG}
          />
        </DelayTimeWrapper>
      </FormField>

      <ToggleSection
        isActive={isDelayedFurther}
        isReadOnly={isTileEditorReadOnly}
        onChange={(isActive) => setIsDelayedFurther(isActive)}
        inputId={DELAY_FURTHER_ID}
        infoText="Hold users longer, until a set time"
        labelText="Add extra time"
      >
        <TimeOfDayForm
          date={todTimestamp}
          isReadOnly={isTileEditorReadOnly}
          scheduleType={scheduleType}
          time={todTime}
          onDateUpdate={setTodTimestamp}
          onScheduleTypeUpdate={setScheduleType}
          onTimeUpdate={setTodTime}
        />
      </ToggleSection>

      <ToggleSection
        isActive={daysAreSkipped}
        isReadOnly={isTileEditorReadOnly}
        onChange={(isActive) => setDaysAreSkipped(isActive)}
        inputId={DAYS_SKIPPED_ID}
        infoText="Users can only proceed to the next step on the allowed days of the week (time of day is not affected)"
        labelText="Don't release users on..."
      >
        <SkipDaysOfWeekForm
          isReadOnly={isTileEditorReadOnly}
          skipDaysOfWeek={skipDaysOfWeek}
          onSkipDaysOfWeekUpdate={setSkipDaysOfWeek}
        />
      </ToggleSection>
    </NodeEditor>
  );
};
